/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { PureComponent } from 'react';
import {
    object, string, func, shape, number, bool,
} from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Media from 'react-media';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { main as MainStyle } from '../style/main';

import EventDispatcher from '../state/reducers/EventDispatcher';
import AccountHome from './MyAccount/AccountHome';
import AddressBook from './AddressBook/AddressBook';
import BillingAddress from './BillingAddress/BillingAddress';
import GiftList from './GiftList/GiftList';
import TrackOrder from './TrackOrder/TrackOrder';
import AlertMessage from '../../../components/GraphqlComponents/AlertMessage/AlertMessage';
import Subscriptions from './Subscriptions/Subscriptions';
import SubscriptionsV2 from './SubscriptionsV2';
import ProfileAndPassword from './ProfileAndPassword/ProfileAndPassword';
import ChangePassword from './ChangePassword/ChangePassword';
import ChangeEmail from './ChangeEmail/ChangeEmail';
import EmailPreference from './EmailPreference/EmailPreference';
import Passport from './Passport/Passport';
import UIModal from '../common/UIModal';
import CUSTOM_BREAKPOINTS from '../../../helpers/customBreakpoints';
import { getFeatureFlags } from '../utils/featureSelectors';
import getSiteConfig from '../utils/getSiteConfig';
import configENV from '../config';
import { isEmpty } from '../utils/object';
import { getIsAuthenticatedStatus } from '../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getActiveABTests } from '../../../../state/ducks/App/ducks/ABTesting/ABTesting-Selectors';
import TalonOneBanner from '../common/Desktop/TalonOneBanner';

const { PERSIST_KEY } = configENV;

const styles = ({ typography, palette }) => MainStyle(typography, palette);

const Components = {
    AccountHome,
    GiftList,
    TrackOrder,
    Subscriptions,
    SubscriptionsV2,
    AddressBook,
    BillingAddress,
    ProfileAndPassword,
    ChangePassword,
    ChangeEmail,
    EmailPreference,
    Passport,
};

class MyAccount extends PureComponent {
    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount = () => {
        const { fetchUserProfile } = this.props;
        fetchUserProfile();
    };

    componentDidUpdate(prevProps) {
        const { location, history } = this.props;
        // handle redirect to replacement order on order details page
        if (location.pathname.includes('/orders') && location.pathname !== prevProps.location.pathname) {
            history.go();
        }
    }

    render() {
        const {
            classes, module, userProfile, featureFlags, brandId, history, isAuthenticatedStatus, flags,
        } = this.props;

        let Layout = Components[module];

        const isSubscription = module === 'Subscriptions';
        // if is-account-subscription-v-2-enabled flag is true: enabling V1 and vice versa
        const isSubscriptionV2 = !featureFlags['is-account-subscription-v-2-enabled'] && isSubscription;

        const giftUserV2 = featureFlags['is-giftlist-v2-enabled'] && flags?.experimentGiftListV2 === 'variant' && history?.location?.pathname.includes('/account/gift-list');
        if (isSubscriptionV2) Layout = Components['SubscriptionsV2'];

        const breakpointExpr = `(min-width: ${CUSTOM_BREAKPOINTS.tabLandscape}px)`;
        if (isEmpty(userProfile) || !brandId) return null;
        return (
            <>
                {featureFlags['is-cs-delivery-alert-message-enabled'] && !userProfile?.isAuthenticated && (
                    <AlertMessage queryTitle="My Orders" />
                )}
                {featureFlags['is-cs-delivery-alert-message-enabled'] && userProfile?.isAuthenticated && (
                    <AlertMessage queryTitle="My Orders Signed In" />
                )}
                {/* TalonOne Rewards Banner - first item in desktop menu - self-contained, fetches own data */}
                {featureFlags?.['is-account-talon-one-wallet-brand-enabled']?.[brandId] && (
                    <TalonOneBanner
                        firstName={userProfile?.firstName}
                    />
                )}
                <Media query={breakpointExpr}>
                    {(matches) => (
                        matches ? (
                            <div className={`${giftUserV2 ? classes.giftUserV2 : classes.main} ${isAuthenticatedStatus ? 'no-margin-top' : ''} ${!isSubscription && `desktop ${brandId}`}`}>
                                <Layout userProfile={userProfile} featureFlags={featureFlags} />
                            </div>
                        ) : (
                            <div className={`${giftUserV2 ? classes.giftUserV2 : classes.main}`}>
                                <Layout userProfile={userProfile} featureFlags={featureFlags} />
                            </div>
                        ))}
                </Media>

                <UIModal />
            </>
        );
    }
}

MyAccount.defaultProps = {
    userProfile: {},
    featureFlags: {},
    flags: {},
};

MyAccount.propTypes = {
    classes: object.isRequired,
    module: string.isRequired,
    userProfile: shape({
        accessToken: string,
        idToken: string,
        ContactId: string,
        userType: string,
        userRole: string,
        email: string,
        aud: string,
        iat: number,
        exp: number,
        sub: string,
        firstName: string,
        lastName: string,
        loginsCount: number,
        isAuthenticated: bool,
        isGuestAccount: bool,
    }),
    featureFlags: object,
    brandId: string.isRequired,
    fetchUserProfile: func.isRequired,
    location: object.isRequired,
    history: object.isRequired,
    isAuthenticatedStatus: bool.isRequired,
    flags: object,

};

const mapStateToProps = (state) => {
    /* istanbul ignore next */
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    let featureFlags = {};
    const brandId = getSiteConfig('brand-id') || '';
    const isAuthenticatedStatus = getIsAuthenticatedStatus(state);
    const activeABTests = getActiveABTests(state);
    if (state.app) {
        try {
            featureFlags = {
                ...getFeatureFlags(state),
            };
            // eslint-disable-next-line no-empty
        } catch (ex) { }
    }
    return {
        userProfile: newState.userProfile,
        featureFlags,
        brandId,
        isAuthenticatedStatus,
        activeABTests,
    };
};

const mapDispatchToProps = (dispatch) => ({
    fetchUserProfile: () => dispatch(EventDispatcher.fetchUserProfile()),
});

const enhance = compose(
    withLDConsumer(),
    withRouter,
    withStyles(styles, { withTheme: true }),
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(MyAccount);
